import { CategoryId } from '$/epg/categories'
import { epgChannelsModel } from '$/epg/channels'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { INITIAL_CHANNELS_LIMIT, INITIAL_CHANNELS_OFFSET } from './index.h'

// load channels by category id
export const load = createEvent<{
  categoryId: number | CategoryId
  regionId?: number
}>()

interface LoadChannelsFxParams {
  id: number | CategoryId
  byCategoryId: number | CategoryId
  offset: number
  limit: number
  regionId?: number
}

const loadChannelsFx = createEffect((params: LoadChannelsFxParams) => {
  const { id } = params

  switch (true) {
    case id === CategoryId.All:
      return epgChannelsModel.loadAllPageFx(params)
    case id === CategoryId.Favorite:
      return epgChannelsModel.loadFavoritesPageFx(params)
    case typeof id === 'number':
      return epgChannelsModel.loadByCategoryPageFx(params)
    default:
      throw new Error(`Unsupported category ID: ${id}`)
  }
})

// load initial channels list effect
export const loadInitialChannelsFx = attach({
  mapParams: ({
    categoryId,
    regionId,
  }: {
    categoryId: number | CategoryId
    regionId?: number
  }) => ({
    byCategoryId: categoryId,
    id: categoryId,
    offset: INITIAL_CHANNELS_OFFSET,
    limit: INITIAL_CHANNELS_LIMIT,
    regionId,
  }),
  effect: loadChannelsFx,
})

// currently loading categories
export const $pending = createStore<[Map<number | CategoryId, boolean>]>([
  new Map(),
])

// add category to pending
sample({
  clock: loadInitialChannelsFx,
  source: $pending,
  fn: (pending, { categoryId }) => {
    const [map] = pending
    map.set(categoryId, true)
    return [map] as const
  },
  target: $pending,
})

// remove category from pending upon request completion
sample({
  clock: loadInitialChannelsFx.finally,
  source: $pending,
  fn: (pending, { params: { categoryId } }) => {
    const [map] = pending
    map.delete(categoryId)
    return [map] as const
  },
  target: $pending,
})

// load channels on category change
sample({
  clock: load,
  source: {
    byCategory: epgChannelsModel.$byCategory,
    pending: $pending,
  },
  filter: ({ byCategory: [channels, meta], pending }, { categoryId }) => {
    if (pending[0].get(categoryId)) return false // already loading channels for this category

    const loadedCount = channels.get(categoryId)?.length ?? 0
    const isLast = Boolean(meta.get(categoryId)?.isLast)
    return loadedCount < INITIAL_CHANNELS_LIMIT && !isLast
  },
  fn: (_, { categoryId, regionId }) => ({ categoryId, regionId }),
  target: loadInitialChannelsFx,
})
